"use client";

import Image from "next/image";
import useCountries from "@/app/hooks/useCountries";
import { SafeUser } from "@/app/types";
import Heading from "../Heading";
import HeartButton from "../HeartButton";
import ShareModal from "../modals/ShareModal";
import { FiShare } from "react-icons/fi";
import { FaStar } from "react-icons/fa";
import useShareModal from "@/app/hooks/useShareModal";
import CertificationBadge from "./CertificationBadge";

interface ListingHeadProps {
  title: string;
  locationValue: string;
  imageSrc: string;
  id: string;
  currentUser?: SafeUser | null;
  /** Listing category + minimum certification, for the "No experience needed" / "<cert> required" badge. */
  category?: string | null;
  minCertification?: string | null;
  /** Dive center running the activity, shown right under the title. */
  operatorName?: string | null;
  /** Center rating; hidden when 0 (no verifiable reviews). */
  rating?: number;
  reviewsNumber?: number;
}

const ListingHead: React.FC<ListingHeadProps> = ({
  title,
  locationValue,
  imageSrc,
  id,
  currentUser,
  category,
  minCertification,
  operatorName,
  rating = 0,
  reviewsNumber = 0,
}) => {
  const { getByName } = useCountries();
  const location = getByName(locationValue);
  const shareModal = useShareModal();

  const openShareModal = () => {
    shareModal.onOpen();
  };

  return (
    <>
      <div className="flex justify-between items-center mb-2">
        <Heading
          title={title}
          subtitle={`${location?.region}, ${location?.label}`}
        />
        <div>
          <button
            onClick={openShareModal}
            className="flex items-center text-neutral-700 text-sm font-bold hover:underline focus:underline"
          >
            <FiShare size={18} className="text-neutral-700 mr-2" />
            Share
          </button>
          <ShareModal 
            title={title} 
            url={typeof window !== 'undefined' ? window.location.href : ''} 
          />
        </div>
      </div>
      {operatorName && (
        <div className="mb-3 flex flex-wrap items-center gap-x-3 gap-y-1 text-neutral-800 md:hidden">
          <span>
            by <span className="font-semibold capitalize">{operatorName}</span>
          </span>
          {rating > 0 && (
            <a
              href="#reviews"
              className="flex items-center gap-1 text-sm font-semibold hover:underline"
            >
              <FaStar size={13} className="text-yellow-500" />
              {rating.toFixed(1)}
              {reviewsNumber > 0 && (
                <span className="font-light text-neutral-500">
                  ({reviewsNumber})
                </span>
              )}
            </a>
          )}
        </div>
      )}
      <div className="mb-4">
        <CertificationBadge
          category={category}
          minCertification={minCertification}
          variant="detail"
        />
      </div>
      <div className="w-full aspect-[4/3] overflow-hidden rounded-xl relative bg-neutral-100">
        <Image
          src={imageSrc || '/images/no-photo-yet.jpg'}
          fill
          className="object-cover object-center w-full"
          alt="Listing image"
          priority
          loading="eager"
          sizes="(max-width: 768px) 100vw, (max-width: 1200px) 80vw, 75vw"
          placeholder="blur"
          blurDataURL="data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHdpZHRoPSIxMDAiIGhlaWdodD0iMTAwIj48cmVjdCB3aWR0aD0iMTAwIiBoZWlnaHQ9IjEwMCIgZmlsbD0iI2VlZWVlZSIvPjwvc3ZnPg=="
        />
        {!currentUser || currentUser.userType === "Diver" ? (
          <div className="absolute top-5 right-5">
            <HeartButton listingId={id} currentUser={currentUser} />
          </div>
        ) : null}
      </div>
    </>
  );
};

export default ListingHead;